import { ArrowLeft, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Clock, Droplets, FlaskConical, Minus, Plus, Shield, Sparkles, Star, Trash2, Zap } from 'lucide-react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import ProductCard from '../../components/ProductCard/ProductCard';
import { useCart } from '../../context/CartContext';
import {
  createProductReview,
  fetchFeatured,
  fetchProductBySlug,
  fetchProductReviews,
  postReviewHelpful,
  postReviewReport,
} from '../../services/api';
import { getSession } from '../../utils/authStorage';
import './ProductDetail.css';

const ProductDetail = () => {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [isReviewFormOpen, setIsReviewFormOpen] = useState(false);
  const [overallRating, setOverallRating] = useState(5);
  const [toastMsg, setToastMsg] = useState('');
  const relatedListRef = useRef(null);
  const ingCarouselTrackRef = useRef(null);
  const [ingredientSlide, setIngredientSlide] = useState(0);
  const [ingCarouselOffset, setIngCarouselOffset] = useState(0);
  const [ingredientsModalOpen, setIngredientsModalOpen] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviewsList, setReviewsList] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsFetchError, setReviewsFetchError] = useState(null);
  const [reviewsHydrated, setReviewsHydrated] = useState(false);
  const [starFilter, setStarFilter] = useState(null);
  const [reviewTitleInput, setReviewTitleInput] = useState('');
  const [reviewContentInput, setReviewContentInput] = useState('');
  const [recommendYes, setRecommendYes] = useState(true);
  const [reviewPhotos, setReviewPhotos] = useState([]);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewBusyId, setReviewBusyId] = useState(null);
  const photoInputRef = useRef(null);

  const reviewPhotoPreviewUrls = useMemo(
    () => reviewPhotos.map((file) => URL.createObjectURL(file)),
    [reviewPhotos]
  );

  useEffect(() => {
    return () => {
      reviewPhotoPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [reviewPhotoPreviewUrls]);

  useEffect(() => {
    fetchProductBySlug(slug).then(data => {
      if (data) {
        setProduct(data);
      }
    }).catch(err => {
      setProduct(null);
    });
    window.scrollTo(0, 0);
    setActiveImageIndex(0);
    setIngredientSlide(0);
    setReviewsList([]);
    setReviewsHydrated(false);
    setReviewsFetchError(null);
    setStarFilter(null);
  }, [slug, i18n.language]);

  useEffect(() => {
    if (!product?.id) return undefined;
    let cancelled = false;
    setReviewsLoading(true);
    setReviewsFetchError(null);
    fetchProductReviews(product.id)
      .then((rows) => {
        if (!cancelled) {
          setReviewsList(Array.isArray(rows) ? rows : []);
          setReviewsHydrated(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setReviewsFetchError(t('productDetail.reviewsLoadError'));
          setReviewsList([]);
          setReviewsHydrated(true);
        }
      })
      .finally(() => {
        if (!cancelled) setReviewsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [product?.id, t]);

  const starCounts = useMemo(() => {
    const c = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviewsList.forEach((r) => {
      const s = Math.round(Number(r.rating_overall));
      if (s >= 1 && s <= 5) c[s] += 1;
    });
    return c;
  }, [reviewsList]);

  const maxStarBarCount = useMemo(
    () => Math.max(1, ...[1, 2, 3, 4, 5].map((k) => starCounts[k])),
    [starCounts]
  );

  const reviewsAvgFromList = useMemo(() => {
    if (!reviewsList.length) return null;
    const sum = reviewsList.reduce((a, r) => a + (Number(r.rating_overall) || 0), 0);
    return sum / reviewsList.length;
  }, [reviewsList]);

  const displayedRating = reviewsAvgFromList ?? Number(product?.rating ?? 0);
  const reviewCountDisplay = reviewsHydrated ? reviewsList.length : (product?.reviews ?? 0);

  const displayedReviews = useMemo(() => {
    if (starFilter == null) return reviewsList;
    return reviewsList.filter((r) => Math.round(Number(r.rating_overall)) === starFilter);
  }, [reviewsList, starFilter]);

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!product?.id) return;
    if (!getSession()?.access) {
      setToastMsg(t('productDetail.reviewLoginRequired'));
      setTimeout(() => setToastMsg(''), 4000);
      return;
    }
    if (!overallRating || !reviewTitleInput.trim() || !reviewContentInput.trim()) {
      setToastMsg(t('productDetail.reviewFillRequired'));
      setTimeout(() => setToastMsg(''), 4000);
      return;
    }
    setSubmittingReview(true);
    try {
      let created;
      if (reviewPhotos.length > 0) {
        const fd = new FormData();
        fd.append('title', reviewTitleInput.trim());
        fd.append('content', reviewContentInput.trim());
        fd.append('rating_overall', String(overallRating));
        fd.append('rating_quality', '5');
        fd.append('rating_value', '5');
        fd.append('is_recommended', recommendYes ? 'true' : 'false');
        reviewPhotos.forEach((file) => fd.append('upload_imgs', file));
        created = await createProductReview(product.id, { formData: fd });
      } else {
        created = await createProductReview(product.id, {
          json: {
            title: reviewTitleInput.trim(),
            content: reviewContentInput.trim(),
            rating_overall: overallRating,
            rating_quality: 5,
            rating_value: 5,
            is_recommended: recommendYes,
          },
        });
      }
      setReviewsList((prev) => [created, ...prev]);
      setReviewsHydrated(true);
      setToastMsg(t('productDetail.reviewThankYou'));
      setIsReviewFormOpen(false);
      setOverallRating(5);
      setReviewTitleInput('');
      setReviewContentInput('');
      setRecommendYes(true);
      setReviewPhotos([]);
      if (photoInputRef.current) photoInputRef.current.value = '';
      setTimeout(() => setToastMsg(''), 3000);
    } catch (err) {
      setToastMsg(err?.message || t('productDetail.reviewSubmitError'));
      setTimeout(() => setToastMsg(''), 5000);
    } finally {
      setSubmittingReview(false);
    }
  };

  const fileSignature = (f) => `${f.name}|${f.size}|${f.lastModified}`;

  const handleReviewPhotosChange = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = '';
    if (picked.length === 0) return;
    setReviewPhotos((prev) => {
      const existing = new Set(prev.map(fileSignature));
      const next = [...prev];
      for (const file of picked) {
        if (next.length >= 6) break;
        const sig = fileSignature(file);
        if (existing.has(sig)) continue;
        existing.add(sig);
        next.push(file);
      }
      return next;
    });
  };

  const removeReviewPhoto = (index) => {
    setReviewPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const handleHelpful = async (reviewId, vote) => {
    if (!getSession()?.access) {
      setToastMsg(t('productDetail.reviewActionLogin'));
      setTimeout(() => setToastMsg(''), 4000);
      return;
    }
    setReviewBusyId(reviewId);
    try {
      const data = await postReviewHelpful(reviewId, vote);
      setReviewsList((prev) =>
        prev.map((r) => {
          if (r.id !== reviewId) return r;
          const next = {
            ...r,
            helpful_yes_count: data.helpful_yes_count,
            helpful_no_count: data.helpful_no_count,
          };
          if (Object.prototype.hasOwnProperty.call(data, 'user_vote')) {
            next.user_helpful_vote = data.user_vote;
          }
          return next;
        })
      );
    } catch {
      setToastMsg(t('productDetail.reviewSubmitError'));
      setTimeout(() => setToastMsg(''), 4000);
    } finally {
      setReviewBusyId(null);
    }
  };

  const handleReport = async (reviewId) => {
    if (!getSession()?.access) {
      setToastMsg(t('productDetail.reviewActionLogin'));
      setTimeout(() => setToastMsg(''), 4000);
      return;
    }
    setReviewBusyId(reviewId);
    try {
      const data = await postReviewReport(reviewId);
      setReviewsList((prev) =>
        prev.map((r) => {
          if (r.id !== reviewId) return r;
          const next = { ...r, report_count: data.report_count };
          if (Object.prototype.hasOwnProperty.call(data, 'user_has_reported')) {
            next.user_has_reported = data.user_has_reported;
          }
          return next;
        })
      );
      setToastMsg(t('productDetail.reportThankYou'));
      setTimeout(() => setToastMsg(''), 3000);
    } catch {
      setToastMsg(t('productDetail.reviewSubmitError'));
      setTimeout(() => setToastMsg(''), 4000);
    } finally {
      setReviewBusyId(null);
    }
  };

  const recalcIngCarouselOffset = useCallback(() => {
    const track = ingCarouselTrackRef.current;
    if (!track) return;
    const card = track.children[ingredientSlide];
    setIngCarouselOffset(card ? card.offsetLeft : 0);
  }, [ingredientSlide]);

  useLayoutEffect(() => {
    recalcIngCarouselOffset();
  }, [recalcIngCarouselOffset, product?.details?.ingredients]);

  useEffect(() => {
    window.addEventListener('resize', recalcIngCarouselOffset);
    return () => window.removeEventListener('resize', recalcIngCarouselOffset);
  }, [recalcIngCarouselOffset]);

  useEffect(() => {
    const n = product?.details?.ingredients?.length ?? 0;
    if (n > 0 && ingredientSlide > n - 1) setIngredientSlide(n - 1);
  }, [product?.details?.ingredients, ingredientSlide]);

  useEffect(() => {
    fetchFeatured().then(data => {
      if (data && Array.isArray(data)) {
        // Loại trừ sản phẩm hiện tại
        const filteredProducts = data.filter(p => p.id !== product?.id);

        // Random từ danh sách đã lọc
        const shuffled = [...filteredProducts];
        for (let i = shuffled.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }

        // Lấy 4 sản phẩm, nếu không đủ thì lấy tất cả
        const randomProducts = shuffled.slice(0, Math.min(4, shuffled.length));

        setRelatedProducts(randomProducts);
      } else {
        setRelatedProducts([]);
      }
    }).catch(err => {
      setRelatedProducts([]);
    });
    window.scrollTo(0, 0);
  }, [product, i18n.language]);

  const galleryImages = useMemo(() => {
    if (!product) return [];
    if (product.gallery?.length > 0) {
      return product.gallery.map(img => img.url);
    }
    return [product.imageA, product.imageB].filter(Boolean);
  }, [product]);

  const galleryCaptions = useMemo(() => {
    if (product?.gallery && product.gallery.length > 0) {
      return product.gallery.map(img => img.label);
    }
    return [];
  }, [product]);

  // Fallback for sections that now may miss dedicated image fields after schema changes.
  const thirdProductImage = galleryImages[2] || galleryImages[0] || product?.imageA || product?.imageB || '';

  if (!product) {
    return (
      <div className="container" style={{ padding: '64px 0', textAlign: 'center' }}>
        <h2>{t('productDetail.notFound')}</h2>
        <Link to="/best-seller" className="btn-primary" style={{ marginTop: '24px', display: 'inline-block' }}>
          <ArrowLeft size={18} /> {t('productDetail.backToList')}
        </Link>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 3000);
  };

  return (
    <div className="product-detail-page">
      <div className="container">
        <div className="breadcrumb">
          <Link to="/">{t('productDetail.home')}</Link>
          <ChevronRight size={14} />
          <Link to="/products">{t('productDetail.skincare')}</Link>
          <ChevronRight size={14} />
          <span>{product.name}</span>
        </div>

        {/* ===== 3-COLUMN HERO: thumbnails | ảnh lớn | thông tin ===== */}
        <div className="pdh-layout">

          {/* COL 1: Thumbnail strip dọc với mũi tên điều hướng */}
          <div className="pdh-thumbs">
            <button
              className="pdh-thumb-nav"
              onClick={() => setActiveImageIndex(Math.max(0, activeImageIndex - 1))}
              disabled={activeImageIndex === 0}
              aria-label="Ảnh trước"
            >
              <ChevronUp size={16} />
            </button>

            <div className="pdh-thumbs-list">
              {galleryImages.map((src, idx) => (
                <button
                  key={idx}
                  className={`pdh-thumb ${idx === activeImageIndex ? 'active' : ''}`}
                  onClick={() => setActiveImageIndex(idx)}
                >
                  <img src={src} alt={`Ảnh ${idx + 1}`} />
                </button>
              ))}
            </div>

            <button
              className="pdh-thumb-nav"
              onClick={() => setActiveImageIndex(Math.min(galleryImages.length - 1, activeImageIndex + 1))}
              disabled={activeImageIndex === galleryImages.length - 1}
              aria-label="Ảnh tiếp theo"
            >
              <ChevronDown size={16} />
            </button>
          </div>

          {/* COL 2: Ảnh chính + caption dưới */}
          <div className="pdh-img-col">
            <div
              className="pdh-main-img-wrap"
              onClick={() => setLightboxOpen(true)}
              title="Nhấp để phóng to"
            >
              {product.isBestSeller && (
                <div className="pdh-img-badge">BEST SELLER</div>
              )}
              <img
                src={galleryImages[activeImageIndex] || product.imageA}
                alt={product.name}
                className="pdh-main-img"
                loading="eager"
              />
              <div className="pdh-zoom-hint">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="11" y1="8" x2="11" y2="14" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              </div>
            </div>

            {/* Caption dưới ảnh */}
            {galleryCaptions[activeImageIndex] && (
              <p className="pdh-caption-below">{galleryCaptions[activeImageIndex]}</p>
            )}
          </div>

          {/* COL 3: Thông tin sản phẩm */}
          <div className="pdh-info">



            {/* Tên sản phẩm */}
            <h1 className="pdh-title">{product.name}</h1>

            {/* Mô tả ngắn */}
            {(product.shortDesc || product.description) && (
              <p className="pdh-subtitle">
                {product.shortDesc || product.description}
              </p>
            )}

            {/* Số lượng */}
            <div className="pdh-qty-row">
              <span className="pdh-section-label">{t('productDetail.quantity')}</span>
              <div className="pdh-qty">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))}><Minus size={14} /></button>
                <span>{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)}><Plus size={14} /></button>
              </div>
            </div>

            {/* Nút CTA */}
            <div className="pdh-cta">
              <button
                className={`pdh-add-btn ${addedToCart ? 'added' : ''}`}
                onClick={handleAddToCart}
              >
                {addedToCart ? (
                  <><Check size={17} /> {t('productDetail.added')}</>
                ) : t('productDetail.addToCart')}
              </button>
              <button className="pdh-buy-btn" onClick={handleAddToCart}>
                {t('productDetail.buyNow')}
              </button>
            </div>

          </div>{/* /pdh-info */}
        </div>{/* /pdh-layout */}

        {/* ===== LIGHTBOX FULLSCREEN ===== */}
        {lightboxOpen && (
          <div
            className="pdh-lightbox-overlay"
            onClick={() => setLightboxOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setLightboxOpen(false)}
            tabIndex={0}
            role="dialog"
            aria-modal="true"
          >
            {/* Ảnh trung tâm */}
            <div className="pdh-lightbox-inner" onClick={(e) => e.stopPropagation()}>
              <img
                src={galleryImages[activeImageIndex] || product.imageA}
                alt={product.name}
                className="pdh-lightbox-img"
              />
              {galleryCaptions[activeImageIndex] && (
                <p className="pdh-lightbox-caption">{galleryCaptions[activeImageIndex]}</p>
              )}
            </div>

            {/* Prev */}
            {activeImageIndex > 0 && (
              <button
                className="pdh-lightbox-nav prev"
                onClick={(e) => { e.stopPropagation(); setActiveImageIndex(activeImageIndex - 1); }}
                aria-label="Ảnh trước"
              >
                <ChevronUp size={28} style={{ transform: 'rotate(-90deg)' }} />
              </button>
            )}

            {/* Next */}
            {activeImageIndex < galleryImages.length - 1 && (
              <button
                className="pdh-lightbox-nav next"
                onClick={(e) => { e.stopPropagation(); setActiveImageIndex(activeImageIndex + 1); }}
                aria-label="Ảnh tiếp theo"
              >
                <ChevronDown size={28} style={{ transform: 'rotate(-90deg)' }} />
              </button>
            )}

            {/* Nút đóng */}
            <button
              className="pdh-lightbox-close"
              onClick={() => setLightboxOpen(false)}
              aria-label="Đóng"
            >
              ✕
            </button>

            {/* Chỉ số ảnh */}
            <div className="pdh-lightbox-counter">
              {activeImageIndex + 1} / {galleryImages.length}
            </div>
          </div>
        )}

        {/* DETAILED SECTIONS */}
        <div className="product-details-structured">
          {/* CÔNG DỤNG */}
          {product.details?.benefits && (
            <section className="pd-section pd-benefits-dark">
              <div className="container">
                <h2 className="pd-section-title white">{product.name} - {t('productDetail.benefits')}</h2>
                <div className="pd-benefits-icon-grid">
                  {product.details.benefits.map((b, i) => {
                    const icons = [<Shield size={80} />, <Sparkles size={80} />, <Droplets size={80} />, <Zap size={80} />, <Clock size={80} />, <FlaskConical size={80} />];
                    return (
                      <div key={i} className="benefit-icon-item">
                        <div className="benefit-icon-wrapper">
                          {icons[i % icons.length]}
                        </div>
                        <p>{b}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}

          {/* KHOA HỌC & KIỂM CHỨNG */}
          {product.details?.science && (
            <section className="pd-section pd-science-proof">
              <div className="container">
                <div className="pd-science-brand">{product.name}</div>
                <h2 className="pd-section-title large">{t('productDetail.scienceProof')}</h2>

                <div className="pd-science-grid-main">
                  <div className="pd-science-left">
                    <h3 className="pd-sub-title">{t('productDetail.visibleResults')}</h3>
                    <p className="pd-science-intro">{t('productDetail.clinicalImproves')}</p>
                    <p className="pd-science-detail">
                      {product.details.science.result_text || t('productDetail.scienceDefault')}
                    </p>
                    <ul className="pd-science-list">
                      {product.details.science.clinical_results.map((res, i) => (
                        <li key={i}>• {res.label} {res.value}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="pd-science-right">
                    <div className="pd-before-after-card">
                      <img
                        src={product.details.science.before_after_image || thirdProductImage}
                        alt="Before/After"
                      />
                    </div>
                  </div>
                </div>

                <div className="pd-science-stats-row">
                  {product.details.science.clinical_results.slice(0, 3).map((res, i) => (
                    <div key={i} className="pd-science-stat-item">
                      <div className="stat-value">
                        {res.value.replace('%', '').replace('+', '').replace('-', '')}
                      </div>
                      <div className="stat-label">{res.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* CƠ CHẾ KHOA HỌC */}
          {product.details?.mechanisms && (
            <section className="pd-section pd-mechanisms-accordion">
              <div className="container">
                <h2 className="pd-section-title">{t('productDetail.mechanism')} {product.name}</h2>
                <div className="pd-mechanism-list">
                  {product.details.mechanisms.map((mech, i) => (
                    <div key={i} className="pd-mechanism-item">
                      <div className="pd-mechanism-body pd-mechanism-body--expanded">
                        <div className={`pd-mechanism-grid ${i % 2 === 1 ? 'pd-mechanism-grid--reverse' : ''}`}>
                          <div className="pd-mechanism-image">
                            <img src={mech.image || product.imageB} alt={mech.title} />
                          </div>
                          <div className="pd-mechanism-info">
                            <h3>{mech.title}</h3>
                            <p>{mech.content || mech.description}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* THÀNH PHẦN */}
          {product.details?.ingredients && (
            <section className="pd-section pd-ingredients-split">
              <div className="container">
                <div className="pd-ingredients-grid">
                  <div className="pd-ingredients-left">
                    <h2 className="pd-ingredients-title">{t('productDetail.ingredients')}</h2>
                    <div className="pd-ingredients-icons">
                      {product.details.ingredients
                        .filter((ing) => typeof ing === 'object' && ing !== null && ing.image)
                        .map((ing, i) => (
                          <div key={i} className="ingredient-feature">
                            <span>{ing.name}</span>
                          </div>
                        ))}
                    </div>
                    <button type="button" className="full-ing-link" onClick={() => setIngredientsModalOpen(true)}>{t('productDetail.seeFullList')}</button>
                  </div>
                  <div className="pd-ingredients-right">
                    <div className="ingredients-carousel-wrap">
                      {(() => {
                        const ingredientsWithImage = product.details.ingredients.filter(
                          (ing) => typeof ing === 'object' && ing !== null && ing.image
                        );
                        if (ingredientsWithImage.length === 0) return null;
                        return (
                          <>
                            <div className="ingredients-carousel-viewport-row">
                              <div className="ingredients-carousel-viewport">
                                <div
                                  ref={ingCarouselTrackRef}
                                  className="ingredients-carousel-track"
                                  style={{ transform: `translateX(-${ingCarouselOffset}px)` }}
                                >
                                  {ingredientsWithImage.map((ing, i) => {
                                    const name = ing.name;
                                    const desc = ing.description || `${name} cung cấp các dưỡng chất thiết yếu cho da.`;
                                    return (
                                      <div key={i} className="ingredient-card">
                                        <div className="ing-card-media">
                                          <img src={ing.image} alt={name} />
                                        </div>
                                        <div className="ing-card-body">
                                          <h4>{name}</h4>
                                          <p>{desc}</p>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                            {ingredientsWithImage.length > 1 ? (
                              <div className="ingredients-carousel-nav">
                                <button
                                  type="button"
                                  className="ingredients-carousel-btn ingredients-carousel-btn--prev"
                                  aria-label={t('productDetail.prevIngredient')}
                                  disabled={ingredientSlide <= 0}
                                  onClick={() => setIngredientSlide((s) => Math.max(0, s - 1))}
                                >
                                  <ChevronLeft size={16} strokeWidth={2} aria-hidden />
                                </button>
                                <div className="ingredients-carousel-dots" role="tablist" aria-label={t('productDetail.ingredients')}>
                                  {ingredientsWithImage.map((_, i) => (
                                    <button
                                      key={i}
                                      type="button"
                                      role="tab"
                                      aria-selected={i === ingredientSlide}
                                      aria-label={`${i + 1} / ${ingredientsWithImage.length}`}
                                      className={`ingredients-carousel-dot ${i === ingredientSlide ? 'is-active' : ''}`}
                                      onClick={() => setIngredientSlide(i)}
                                    />
                                  ))}
                                </div>
                                <button
                                  type="button"
                                  className="ingredients-carousel-btn ingredients-carousel-btn--next"
                                  aria-label={t('productDetail.nextIngredient')}
                                  disabled={ingredientSlide >= ingredientsWithImage.length - 1}
                                  onClick={() => setIngredientSlide((s) => Math.min(ingredientsWithImage.length - 1, s + 1))}
                                >
                                  <ChevronRight size={16} strokeWidth={2} aria-hidden />
                                </button>
                              </div>
                            ) : null}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {/* HƯỚNG DẪN SỬ DỤNG */}
          {product.details?.usage && (
            <section className="pd-section last-pd-section">
              <div className="container">
                <h2 className="pd-section-title">{t('productDetail.usage')}</h2>
                <div className="pd-usage-layout">
                  <div className="pd-usage-steps">
                    {product.details.usage.steps.map((step, i) => (
                      <div key={i} className="usage-step-item">
                        <div className="step-num">{i + 1}</div>
                        <p>{step}</p>
                      </div>
                    ))}
                    {product.details.usage.note && (
                      <p className="pd-usage-note"><i>* {product.details.usage.note}</i></p>
                    )}
                  </div>
                  {/* Reusing common step image if available */}
                  <div className="pd-usage-image">
                    <img src={product.details.usage.image || thirdProductImage} alt="Usage Steps" />
                  </div>
                </div>
              </div>
            </section>
          )}
        </div>

        {relatedProducts.length > 0 && (
          <section className="related-section">
            <h2 className="detail-section-title">{t('productDetail.related')}</h2>
            <div className="related-scroller">
              <button
                type="button"
                className="related-arrow related-arrow-prev"
                onClick={() => {
                  if (relatedListRef.current) {
                    relatedListRef.current.scrollBy({ left: -220, behavior: 'smooth' });
                  }
                }}
                aria-label="Previous related products"
              >
                ‹
              </button>
              <div className="related-track" ref={relatedListRef}>
                {relatedProducts.map((item) => (
                  <div key={item.id} className="related-item">
                    <ProductCard product={item} />
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="related-arrow related-arrow-next"
                onClick={() => {
                  if (relatedListRef.current) {
                    relatedListRef.current.scrollBy({ left: 220, behavior: 'smooth' });
                  }
                }}
                aria-label="Next related products"
              >
                ›
              </button>
            </div>
          </section>
        )}

        {false && (
          <section className="reviews-section">
            <h2 className="detail-section-title">{t('productDetail.reviewsTitle')}</h2>

            <div className="reviews-top-row">
              <div>
                <p className="reviews-subtitle">{t('productDetail.reviewSummary')}</p>
                <p className="reviews-note">{t('productDetail.reviewFilterHint')}</p>
              </div>
              <button
                type="button"
                className="btn-secondary reviews-write-btn"
                onClick={() => setIsReviewFormOpen((open) => !open)}
              >
                {isReviewFormOpen ? t('productDetail.closeForm') : t('productDetail.writeReview')}
              </button>
            </div>

            <p className="reviews-subtitle reviews-rating-summary-heading">{t('productDetail.avgRating')}</p>
            <div className="reviews-columns">
              <div className="reviews-snapshot">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div
                    key={star}
                    role="button"
                    tabIndex={0}
                    className={`reviews-bar-row${starFilter === star ? ' reviews-bar-row--active' : ''}`}
                    onClick={() => setStarFilter((prev) => (prev === star ? null : star))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setStarFilter((prev) => (prev === star ? null : star));
                      }
                    }}
                  >
                    <span className="reviews-bar-label">{star}★</span>
                    <div className="reviews-bar-track">
                      <div
                        className="reviews-bar-fill"
                        style={{ width: `${(starCounts[star] / maxStarBarCount) * 100}%` }}
                      />
                    </div>
                    <span className="reviews-bar-count">{starCounts[star]}</span>
                  </div>
                ))}
              </div>

              <div className="reviews-average">
                <div className="reviews-average-card">
                  <div
                    className="reviews-score-ring"
                    role="img"
                    aria-label={`${t('productDetail.overall')} ${(Number.isFinite(displayedRating) ? displayedRating : 0).toFixed(1)} / 5`}
                  >
                    <span className="reviews-score-ring__value">
                      {(Number.isFinite(displayedRating) ? displayedRating : 0).toFixed(1)}
                    </span>
                  </div>
                  <div className="reviews-average-meta">
                    <span className="reviews-average-meta__label">{t('productDetail.overall')}</span>
                    <div className="reviews-average-meta__stars" aria-hidden>
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          fill={i < Math.floor(Number.isFinite(displayedRating) ? displayedRating : 0) ? 'var(--color-star-gold)' : 'none'}
                          color="var(--color-star-gold)"
                        />
                      ))}
                    </div>
                    <p className="reviews-average-meta__count">
                      {reviewCountDisplay} {t('productDetail.reviews')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {isReviewFormOpen && (
              <div className="review-form-wrap">
                {!getSession()?.access && (
                  <p className="reviews-login-hint">
                    {t('productDetail.reviewLoginHint')}{' '}
                    <Link to="/login">{t('productDetail.goToLogin')}</Link>
                  </p>
                )}
                <form className="review-form" onSubmit={handleReviewSubmit}>
                  <div className="review-form-row rating-row">
                    <label className="review-form-label">{t('productDetail.overallRating')}</label>
                    <div className="rating-stars-group">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          className={`rating-star-btn ${overallRating >= star ? 'active' : ''}`}
                          onClick={() => setOverallRating(star)}
                        >
                          <Star
                            size={18}
                            fill={overallRating >= star ? 'var(--color-star-gold)' : 'none'}
                            color="var(--color-star-gold)"
                          />
                        </button>
                      ))}
                      <span className="rating-label">
                        {overallRating === 1 && t('productDetail.ratingBad')}
                        {overallRating === 2 && t('productDetail.ratingAverage')}
                        {overallRating === 3 && t('productDetail.ratingOkay')}
                        {overallRating === 4 && t('productDetail.ratingGood')}
                        {overallRating === 5 && t('productDetail.ratingExcellent')}
                      </span>
                    </div>
                  </div>
                  <div className="review-form-row review-recommend-row">
                    <span className="review-form-label" id="review-recommend-label">
                      {t('productDetail.recommendProduct')}
                    </span>
                    <div className="review-recommend-btns" role="group" aria-labelledby="review-recommend-label">
                      <button
                        type="button"
                        className={`review-recommend-pill${recommendYes ? ' is-active' : ''}`}
                        onClick={() => setRecommendYes(true)}
                        aria-pressed={recommendYes}
                      >
                        {t('productDetail.yes')}
                      </button>
                      <button
                        type="button"
                        className={`review-recommend-pill${!recommendYes ? ' is-active' : ''}`}
                        onClick={() => setRecommendYes(false)}
                        aria-pressed={!recommendYes}
                      >
                        {t('productDetail.no')}
                      </button>
                    </div>
                  </div>
                  <div className="review-form-row">
                    <label className="review-form-label" htmlFor="review-title-input">{t('productDetail.reviewTitle')}</label>
                    <input
                      id="review-title-input"
                      type="text"
                      className="review-form-input"
                      placeholder={t('productDetail.reviewTitlePlaceholder')}
                      value={reviewTitleInput}
                      onChange={(e) => setReviewTitleInput(e.target.value)}
                    />
                  </div>
                  <div className="review-form-row">
                    <label className="review-form-label" htmlFor="review-content-input">{t('productDetail.reviewContent')}</label>
                    <textarea
                      id="review-content-input"
                      className="review-form-textarea"
                      rows={6}
                      placeholder={t('productDetail.reviewContentPlaceholder')}
                      value={reviewContentInput}
                      onChange={(e) => setReviewContentInput(e.target.value)}
                    />
                  </div>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="review-photo-input-hidden"
                    onChange={handleReviewPhotosChange}
                    aria-label={t('productDetail.addPhoto')}
                  />
                  <div className="review-form-row photo-row">
                    <button
                      type="button"
                      className="review-photo-btn"
                      disabled={reviewPhotos.length >= 6}
                      onClick={() => photoInputRef.current?.click()}
                    >
                      {t('productDetail.addPhoto')}
                    </button>
                    <span className="photo-hint">
                      {t('productDetail.photoLimit')}
                      {reviewPhotos.length > 0 ? ` (${reviewPhotos.length})` : ''}
                    </span>
                  </div>
                  {reviewPhotoPreviewUrls.length > 0 && (
                    <div className="review-form-photo-previews">
                      {reviewPhotoPreviewUrls.map((url, idx) => (
                        <div key={url} className="review-form-photo-thumb-wrap">
                          <img src={url} alt="" className="review-form-photo-thumb" />
                          <button
                            type="button"
                            className="review-form-photo-remove"
                            onClick={() => removeReviewPhoto(idx)}
                            aria-label={t('productDetail.removePhoto')}
                          >
                            <Trash2 size={16} strokeWidth={2} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <button type="submit" className="btn-primary review-form-submit" disabled={submittingReview}>
                    {submittingReview ? t('productDetail.submittingReview') : t('productDetail.submitReview')}
                  </button>
                </form>
              </div>
            )}

            {reviewsFetchError && <p className="reviews-fetch-error">{reviewsFetchError}</p>}
            {reviewsLoading && <p className="reviews-loading">{t('productDetail.reviewsLoading')}</p>}

            <div className="reviews-list">
              {!reviewsLoading && !reviewsFetchError && displayedReviews.length === 0 && (
                <p className="reviews-empty">{t('productDetail.reviewsEmpty')}</p>
              )}
              {displayedReviews.map((rev) => {
                const ro = Math.min(5, Math.max(0, Math.round(Number(rev.rating_overall) || 0)));
                const yesC = Number(rev.helpful_yes_count) || 0;
                const noC = Number(rev.helpful_no_count) || 0;
                const myHelpful = rev.user_helpful_vote;
                const imgs = Array.isArray(rev.imgs) ? rev.imgs : [];
                const busy = reviewBusyId === rev.id;
                return (
                  <article key={rev.id} className="review-item">
                    <div className="review-col meta">
                      <p className="review-user">{rev.user_name || t('productDetail.customer')}</p>
                      <p className="review-location">{t('productDetail.customer')}</p>
                    </div>
                    <div className="review-col content">
                      <header className="review-header">
                        <div className="stars">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              size={14}
                              fill={i < ro ? 'var(--color-star-gold)' : 'none'}
                              color="var(--color-star-gold)"
                            />
                          ))}
                        </div>
                      </header>
                      <h3 className="review-title">{rev.title}</h3>
                      <p className="review-body">{rev.content}</p>
                      {imgs.length > 0 && (
                        <div className="review-photos">
                          {imgs.map((src, idx) => (
                            <img key={`${rev.id}-img-${idx}`} loading="lazy" src={src} alt="" className="review-photo" />
                          ))}
                        </div>
                      )}
                      <p className="review-recommends">
                        {t('productDetail.recommendProduct')}{' '}
                        <span className="check-mark">{rev.is_recommended ? '✓' : '—'}</span>{' '}
                        <strong>{rev.is_recommended ? t('productDetail.yes') : t('productDetail.no')}</strong>
                      </p>
                      <div className="review-helpful">
                        <span>{t('productDetail.helpful')}</span>
                        <button
                          type="button"
                          className={`review-chip${myHelpful === 'yes' ? ' review-chip--helpful-yes' : ''}`}
                          disabled={busy}
                          onClick={() => handleHelpful(rev.id, 'yes')}
                        >
                          {t('productDetail.yes')} · {yesC}
                        </button>
                        <button
                          type="button"
                          className={`review-chip${myHelpful === 'no' ? ' review-chip--helpful-no' : ''}`}
                          disabled={busy}
                          onClick={() => handleHelpful(rev.id, 'no')}
                        >
                          {t('productDetail.no')} · {noC}
                        </button>
                        <button
                          type="button"
                          className={`review-chip${rev.user_has_reported ? ' review-chip--reported' : ''}`}
                          disabled={busy}
                          onClick={() => handleReport(rev.id)}
                        >
                          {t('productDetail.report')}
                          {Number(rev.report_count) > 0 ? ` · ${rev.report_count}` : ''}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        )}


      </div>

      {/* Ingredients Full List Modal */}
      {ingredientsModalOpen && product?.details?.ingredients && (
        <div
          className="ing-modal-overlay"
          onClick={() => setIngredientsModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="ing-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="ing-modal-close"
              onClick={() => setIngredientsModalOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <h2 className="ing-modal-title">Full Ingredient List</h2>
            <p className="ing-modal-text">
              {product.details.ingredients.map((ing) =>
                typeof ing === 'string' ? ing : ing.name
              ).join(', ')}
            </p>
            <p className="ing-modal-disclaimer">
              {t('productDetail.ingredientDisclaimer', {
                defaultValue: 'Please be aware that ingredient lists for the products of our brand are updated regularly. Please refer to the ingredient list on your product package for the most up-to-date list of ingredients to ensure it is suitable for your personal use.'
              })}
            </p>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toastMsg && (
        <div className="pd-toast" role="status" aria-live="polite">
          <span className="pd-toast-icon">
            <svg viewBox="0 0 24 24" width="22" height="22"><path d="M9.2 16.2 4.9 12l1.4-1.4 2.9 2.8 8.5-8.3L19 6.5z" fill="currentColor" /></svg>
          </span>
          <span>{toastMsg}</span>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
